// src/components/RefreshAccessButton.js
//
// "I already paid — why does this still say I haven't?"
//
// The webhook is the only thing that normally grants access, and there are
// several ordinary ways for it not to run: never registered in the Stripe
// Dashboard, the event type not ticked, a rotated signing secret, a failed
// deploy, or a Payment Link purchase that arrived with no metadata for it to
// work with. In all of those the money is in Stripe and the customer is
// looking at a screen calling them unpaid.
//
// This button calls syncMySubscription, which asks Stripe directly and then
// makes the same Firestore writes the webhook would have. It can only ever
// grant what Stripe confirms, and only for the person clicking it.
//
// It renders nothing for anyone the app already considers paid, so it stays
// out of the way of the people who don't need it.

import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/firebaseConfig";
import useAccessLevel from "../hooks/useAccessLevel";

export default function RefreshAccessButton() {
  const { hasPaidSubscription, loading } = useAccessLevel();
  const [state, setState] = useState("idle"); // idle | working | none | error
  const [note, setNote] = useState("");

  if (loading || hasPaidSubscription) return null;

  const run = async () => {
    setState("working");
    setNote("");
    try {
      const syncMySubscription = httpsCallable(functions, "syncMySubscription");
      const { data } = await syncMySubscription();

      if (data?.found) {
        // No navigation and no reload needed: useAccessLevel is listening to
        // both documents, so the badge and the locks update themselves the
        // moment the writes land.
        setState("idle");
        if (data.roleGranted === false) {
          setNote(
            "Subscription found. We still need your state and grade to open your grade-level content."
          );
        }
        return;
      }

      setState("none");
      setNote(
        data?.reason === "no_live_subscription"
          ? "Stripe doesn't show an active subscription for this account."
          : "We couldn't find a Stripe customer for this email address."
      );
    } catch (err) {
      console.error("syncMySubscription failed:", err?.code, err?.message);
      setState("error");
      setNote("Couldn't reach the billing service. Please try again shortly.");
    }
  };

  return (
    <div style={s.wrap}>
      <button
        type="button"
        onClick={run}
        disabled={state === "working"}
        style={s.link}
      >
        {state === "working"
          ? "Checking with Stripe…"
          : "Already subscribed? Refresh my access"}
      </button>
      {note && <span style={s.note}>{note}</span>}
    </div>
  );
}

const s = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    margin: "10px 0 0",
  },
  link: {
    background: "none",
    border: "none",
    padding: 0,
    color: "#7f00ff",
    fontSize: 13.5,
    fontWeight: 600,
    textDecoration: "underline",
    cursor: "pointer",
  },
  note: {
    fontSize: 12.5,
    color: "#6b7280",
    textAlign: "center",
    maxWidth: 420,
    lineHeight: 1.5,
  },
};
